import { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import {
  SchedulerTabsProps,
  SchedulerTankClosingStateRowType,
  SchedulerTanksRowType,
  SchedulerTankWeekStateRowType,
} from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
  useTankClosingState,
} from './Scheduler.data'
import { SchedulerTankWeekStateTable } from './SchedulerTankWeekState.muiTable'
import { WeekSelector } from './WeekSelector.view'
import { TOKENS } from './SchedulerDesign'

/**
 * Per-tank opening stock and closing target, one week at a time.
 *
 * Its own tab rather than a grid on the Tanks tab because it is per-week and
 * that one is not — the same split, for the same reason, as Resource Capacity
 * Profile against Resource & Material Constraints.
 *
 * The panel underneath is the other half of the workflow. A ~5 day hold
 * against a 168-hour week means a fill made this week usually cannot be
 * drained this week, so the feature is a cross-week pipeline whose handoff is
 * two typed numbers. The engine reports what each vessel actually closed with;
 * the planner copies that into the NEXT week's opening quantity. Showing the
 * report beside the field it is copied into is the whole point — deriving
 * "how long has this been held" by hand is exactly the arithmetic that gets
 * silently got wrong.
 */
export const ScheduleTankWeekStateView = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const API = 'tank-week-state'
  const [selectedWeek, setSelectedWeek] = useState(1)

  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration, selectedWeek)
  const { data = [], predicate } = responseData

  const {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  } = useSchedulerTableHandlers(schedulerId, API, iteration, selectedWeek)

  // Every DECLARED vessel appears here, whether or not it has a stored row.
  //
  // A tank with no week-state row is the ordinary case, not an omission: it
  // opens empty and carries no closing target, which is the pure make-to-order
  // shape the consolidated fill derivation handles without any typed numbers.
  // But a planner looking for TANK02 and not finding it cannot tell that from
  // "the tank is missing", and has nowhere to type an opening quantity when
  // they do need one.
  //
  // Editing a placeholder upserts it into existence — the Service merges on
  // `tankId` with `when_not_matched_insert_all`, and strips the synthetic `id`
  // before writing — so nothing needs creating first. Placeholder ids sit above
  // any server-assigned one so they cannot collide.
  const { data: tanksResponse = { data: [] } } =
    useScheduleProductionRequirements(schedulerId, 'tanks', iteration)
  const declaredTanks = (tanksResponse?.data ?? []) as SchedulerTanksRowType[]
  const rows = useMemo(() => {
    const stored = data as SchedulerTankWeekStateRowType[]
    const have = new Set(stored.map((r) => r.tankId))
    const placeholders = declaredTanks
      .filter((t) => t.tankId && !have.has(t.tankId))
      .map((t, i) => ({
        id: 1_000_000 + i,
        tankId: t.tankId,
        productCode: null,
        openingQuantity: null,
        hoursInTankAtStart: null,
        closingTargetQuantity: null,
      }))
    return [...stored, ...placeholders].sort((a, b) =>
      String(a.tankId).localeCompare(String(b.tankId))
    )
  }, [data, declaredTanks])

  const { data: closing = [] } = useTankClosingState(
    schedulerId,
    iteration,
    selectedWeek
  )
  const closingRows = closing as SchedulerTankClosingStateRowType[]

  return (
    <Box
      sx={{ height: 'calc(100vh - 240px)', minHeight: 400, overflowY: 'auto' }}
    >
      <WeekSelector value={selectedWeek} onChange={setSelectedWeek} />
      <Typography
        sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, mt: -0.5 }}
      >
        What is in each vessel at hour 0 of week {selectedWeek}, and what should
        be in it at the end. Hours In Tank At Start is how much of the
        maturation has already elapsed — leave it blank and none is assumed,
        which can only delay a drain, never allow an early one.
      </Typography>
      <SchedulerTankWeekStateTable
        data={rows}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadScheduleData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate}
        deleteTableData={deleteTableData}
        isDeleting={isDeleting}
      />

      {closingRows.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 1.5,
            border: '1px solid',
            borderColor: TOKENS.line300,
            borderRadius: '2px',
          }}
        >
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.5 }}>
            Closing state at the end of week {selectedWeek} (last run)
          </Typography>
          <Typography
            sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}
          >
            Copy these into week {selectedWeek + 1}&rsquo;s Opening Quantity and
            Hours In Tank At Start. Reported by the scheduler, not editable.
          </Typography>
          <Box
            component="table"
            sx={{
              borderCollapse: 'collapse',
              fontSize: '0.72rem',
              '& th, & td': {
                border: '1px solid',
                borderColor: TOKENS.line300,
                padding: '2px 8px',
                textAlign: 'right',
              },
              '& th:first-of-type, & td:first-of-type, & th:nth-of-type(2), & td:nth-of-type(2)':
                { textAlign: 'left' },
            }}
          >
            <thead>
              <tr>
                <th>Tank</th>
                <th>Product</th>
                <th>Closing Quantity</th>
                <th>Hours Held</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {closingRows.map((r) => (
                <tr key={`${r.tankId}-${r.productCode}`}>
                  <td>{r.tankId}</td>
                  <td>{r.productCode}</td>
                  <td>{Math.round(r.closingQuantity)}</td>
                  <td>{r.hoursHeldAtHorizonEnd}</td>
                  <td>{Math.round(r.closingTargetQuantity)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  )
}
